import { useState } from "react"
import { useNavigate } from "react-router"
import styles from "./post-detail-page.module.css"

type TabType = "detail" | "comments"

// SVG Icons
const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
)

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="1"/>
    <circle cx="12" cy="5" r="1"/>
    <circle cx="12" cy="19" r="1"/>
  </svg>
)

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
)

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
)

const HeartFilledIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
)

const CommentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
)

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="18" cy="5" r="3"/>
    <circle cx="6" cy="12" r="3"/>
    <circle cx="18" cy="19" r="3"/>
    <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
  </svg>
)

const BookmarkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
)

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
)

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
)

interface Comment {
  id: string
  author: string
  avatar: string
  text: string
  time: string
  likes: number
}

export default function PostDetailPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabType>("detail")
  const [isLiked, setIsLiked] = useState(false)
  const [commentText, setCommentText] = useState("")
  const [isFollowing, setIsFollowing] = useState(false)

  // Mock data
  const postData = {
    id: "1",
    title: "如何在创业初期找到合适的联合创始人",
    body: `创业的路上，找到一个合适的联合创始人至关重要。以下是一些实用的建议：

**1. 明确你的需求**
在寻找联合创始人之前，先明确你需要什么样的伙伴。技术型？运营型？还是全栈型？清晰的定位能帮助你更精准地筛选。

**2. 从身边的人开始**
最好的联合创始人往往来自你的社交圈——同学、前同事、朋友的朋友。信任基础是长期合作的关键。

**3. 设定试用期**
不要急于确定合作关系。先以项目合作的方式相互了解，观察对方的工作习惯、沟通风格和价值观是否匹配。

**4. 股权分配要公平**
早期股权分配要兼顾激励和公平。建议预留足够的期权池，并为未来的融资留出空间。

**5. 互补性原则**
寻找与你技能互补的伙伴。如果你擅长产品，尝试找技术背景的联合创始人。多元化能让团队走得更远。`,
    coverUrl: "",
    author: {
      id: "u1",
      name: "李明",
      avatar: "",
      bio: "连续创业者，关注教育科技领域",
      followers: 1234,
    },
    tags: ["创业", "团队", "融资", "管理"],
    viewCount: 1256,
    likeCount: 89,
    commentCount: 23,
    createdAt: "2024-05-15",
    isOriginal: true,
  }

  const mockComments: Comment[] = [
    {
      id: "c1",
      author: "王芳",
      avatar: "",
      text: "写得很好！我觉得试用期这个建议特别实用，很多创业者都容易忽略这一点。",
      time: "1小时前",
      likes: 12,
    },
    {
      id: "c2",
      author: "张伟",
      avatar: "",
      text: "补充一点：价值观的契合非常重要，在关键决策时刻可能会决定团队的走向。",
      time: "2小时前",
      likes: 8,
    },
    {
      id: "c3",
      author: "陈静",
      avatar: "",
      text: "关于股权分配，我建议大家参考 YC 的标准模板，会更科学一些。",
      time: "3小时前",
      likes: 15,
    },
  ]

  const handleLike = () => {
    setIsLiked(!isLiked)
  }

  const handleFollow = () => {
    setIsFollowing(!isFollowing)
  }

  const handleComment = () => {
    if (commentText.trim()) {
      // Submit comment
      setCommentText("")
    }
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <div className={styles.headerTitle}>帖子详情</div>
        <div className={styles.headerActions}>
          <button className={styles.actionBtn}>
            <BookmarkIcon />
          </button>
          <button className={styles.actionBtn}>
            <MoreIcon />
          </button>
        </div>
      </div>

      {/* Hero - Cover Image */}
      <div className={styles.hero}>
        <div className={styles.coverWrapper}>
          <div className={styles.cover}>
            {postData.coverUrl ? (
              <img src={postData.coverUrl} alt={postData.title} />
            ) : (
              <div className={styles.coverPlaceholder}>
                <ImageIcon />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Author Info */}
      <div className={styles.authorSection}>
        <div className={styles.authorAvatar}>
          <UserIcon />
        </div>
        <div className={styles.authorMeta}>
          <div className={styles.authorName}>{postData.author.name}</div>
          <div className={styles.authorStats}>
            <ClockIcon />
            <span>{postData.createdAt}</span>
            <span>·</span>
            <span>{postData.viewCount} 阅读</span>
          </div>
        </div>
        <button
          className={`${styles.followBtn} ${isFollowing ? styles.following : ""}`}
          onClick={handleFollow}
        >
          {isFollowing ? "已关注" : "关注"}
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <h1 className={styles.postTitle}>{postData.title}</h1>

        <div className={styles.tags}>
          {postData.tags.map((tag) => (
            <span key={tag} className={styles.tag}>
              #{tag}
            </span>
          ))}
        </div>

        <div className={styles.postBody}>
          {postData.body.split("\n\n").map((para, idx) => (
            <p key={idx}>{para}</p>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "detail" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("detail")}
        >
          详情
        </button>
        <button
          className={`${styles.tab} ${activeTab === "comments" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("comments")}
        >
          评论 ({postData.commentCount})
        </button>
      </div>

      {/* Comments Section */}
      <div className={styles.commentsSection}>
        {activeTab === "comments" && (
          <>
            {mockComments.length > 0 ? (
              <div className={styles.commentList}>
                {mockComments.map((comment) => (
                  <div key={comment.id} className={styles.commentItem}>
                    <div className={styles.commentAvatar}>
                      <UserIcon />
                    </div>
                    <div className={styles.commentContent}>
                      <div className={styles.commentHeader}>
                        <span className={styles.commentName}>{comment.author}</span>
                        <span className={styles.commentTime}>{comment.time}</span>
                      </div>
                      <p className={styles.commentText}>{comment.text}</p>
                      <div className={styles.commentActions}>
                        <span className={styles.commentAction}>
                          <HeartIcon />
                          {comment.likes}
                        </span>
                        <span className={styles.commentAction}>
                          <CommentIcon />
                          回复
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <EmptyIcon />
                </div>
                <h3 className={styles.emptyTitle}>暂无评论</h3>
                <p className={styles.emptyDesc}>成为第一个评论的人</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Comment Input */}
      <div className={styles.commentInput}>
        <input
          type="text"
          className={styles.commentInputField}
          placeholder="写下你的评论..."
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleComment()}
        />
        <button
          className={styles.commentSubmit}
          onClick={handleComment}
          disabled={!commentText.trim()}
        >
          发送
        </button>
      </div>

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <div className={styles.actionStats}>
          <span className={styles.actionStat} onClick={handleLike}>
            {isLiked ? <HeartFilledIcon /> : <HeartIcon />}
            <span className={styles.actionStatValue}>
              {isLiked ? postData.likeCount + 1 : postData.likeCount}
            </span>
          </span>
          <span className={styles.actionStat}>
            <CommentIcon />
            <span className={styles.actionStatValue}>{postData.commentCount}</span>
          </span>
        </div>
        <button className={styles.actionShare}>
          <ShareIcon />
          <span>分享</span>
        </button>
      </div>
    </div>
  )
}